import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Button,
  IconButton,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import ImageNotSupportedOutlinedIcon from "@mui/icons-material/ImageNotSupportedOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ApiConfig from "../../../Config";
import collectionService from "../../../repository/collectionRepository";

const CollectionProducts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    successSoft: isDark ? "rgba(143,198,163,.12)" : "rgba(61,128,84,.09)",
    danger: isDark ? "#D99494" : "#B54545",
    dangerSoft: isDark ? "rgba(217,148,148,.10)" : "rgba(181,69,69,.08)",
    shadow: isDark
      ? "0 10px 35px rgba(0,0,0,.16)"
      : "0 10px 35px rgba(50,40,30,.05)",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: colors.shadow,
  };

  const [collectionData, setCollectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState("");
  const [removeDialog, setRemoveDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [removing, setRemoving] = useState(false);

  const fetchCollectionProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await collectionService.getProductsByCollection(id);
      setCollectionData(res.data?.collectionData || null);
    } catch (err) {
      console.error("Failed to load collection products:", err);
      setError(
        err?.response?.data?.message || "Failed to load collection products",
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCollectionProducts();
  }, [fetchCollectionProducts]);

  useEffect(() => {
    if (!success) return;

    const timer = setTimeout(() => {
      setSuccess("");
    }, 3000);

    return () => clearTimeout(timer);
  }, [success]);

  const handleAddProductClick = () => {
    navigate("/addToCollection", {
      state: {
        collectionId: id,
      },
    });
  };

  const handleBack = () => {
    navigate(-1);
  };

  const openRemoveDialog = (item) => {
    setSelectedProduct(item);
    setRemoveDialog(true);
  };

  const closeRemoveDialog = () => {
    if (removing) return;

    setRemoveDialog(false);
    setSelectedProduct(null);
  };

  const handleRemoveProduct = async () => {
    if (!selectedProduct?._id) return;

    try {
      setRemoving(true);
      setError(null);
      setSuccess("");

      await collectionService.removeProductsFromCollection(id, [
        selectedProduct._id,
      ]);
      setRemoveDialog(false);
      setSelectedProduct(null);

      setSuccess("Product removed from collection");

      await fetchCollectionProducts();
    } catch (err) {
      console.error("Failed to remove product:", err);

      setError(
        err?.response?.data?.message ||
          "Failed to remove product from collection",
      );
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box textAlign="center">
          <CircularProgress
            size={30}
            thickness={3}
            sx={{ color: colors.accent }}
          />

          <Typography
            sx={{
              mt: 1.5,
              fontSize: 12,
              color: colors.secondary,
            }}
          >
            Loading collection...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error && !collectionData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Alert
          severity="error"
          sx={{
            maxWidth: 600,
            mx: "auto",
            borderRadius: "10px",
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!collectionData) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Paper
          sx={{
            ...cardSx,
            p: 5,
            textAlign: "center",
            maxWidth: 420,
          }}
        >
          <Inventory2OutlinedIcon
            sx={{
              fontSize: 42,
              color: colors.secondary,
              mb: 1,
            }}
          />

          <Typography
            sx={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 22,
              fontWeight: 600,
              color: colors.text,
            }}
          >
            Collection not found
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 12,
              color: colors.secondary,
            }}
          >
            The requested collection could not be loaded.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const productList = collectionData.productList || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: colors.background,
        color: colors.text,
        p: {
          xs: 1.5,
          sm: 2.5,
          md: 4,
        },
      }}
    >
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess("")}
          sx={{
            position: "fixed",
            top: 20,
            right: { xs: 12, sm: 24 },
            zIndex: 9999,
            borderRadius: "10px",
            fontSize: 12,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
          }}
        >
          {success}
        </Alert>
      )}

      {error && collectionData && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{
            position: "fixed",
            top: 20,
            right: { xs: 12, sm: 24 },
            zIndex: 9999,
            borderRadius: "10px",
            fontSize: 12,
            boxShadow: "0 8px 30px rgba(0,0,0,.12)",
          }}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 1500,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: {
              xs: 2.5,
              md: 3.5,
            },
            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.4,
              width: "100%",
            }}
          >
            <Tooltip title="Back">
              <IconButton
                onClick={handleBack}
                sx={{
                  width: 38,
                  height: 38,
                  mt: 0.2,
                  flexShrink: 0,
                  borderRadius: "9px",
                  color: colors.secondary,
                  backgroundColor: colors.card,
                  border: `1px solid ${colors.border}`,
                  "&:hover": {
                    color: colors.accent,
                    borderColor: colors.accent,
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                <ArrowBackRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>

            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Inventory2OutlinedIcon sx={{ fontSize: 23 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: {
                    xs: 22,
                    sm: 25,
                    md: 28,
                  },
                  lineHeight: 1.15,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                {collectionData.collectionTitle}
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: colors.secondary,
                  wordBreak: "break-word",
                }}
              >
                Collection products
                {collectionData.collectionId && (
                  <> · ID: {collectionData.collectionId}</>
                )}
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
            onClick={handleAddProductClick}
            fullWidth={isMobile}
            sx={{
              minWidth: {
                sm: 205,
              },
              height: 42,
              px: 2,
              borderRadius: "9px",
              backgroundColor: colors.accent,
              color: "#fff",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 650,
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: isDark ? "#C5AC8C" : "#705B44",
                boxShadow: "none",
              },
            }}
          >
            Add Product
          </Button>
        </Box>

        <Grid
          container
          spacing={1.5}
          sx={{
            mb: {
              xs: 2.5,
              md: 3,
            },
          }}
        >
          <Grid item xs={6} sm={4}>
            <Paper
              sx={{
                ...cardSx,
                p: {
                  xs: 1.5,
                  sm: 2,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: colors.secondary,
                  fontWeight: 600,
                }}
              >
                Products
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 24,
                  lineHeight: 1,
                  fontWeight: 650,
                  color: colors.text,
                }}
              >
                {productList.length}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={6} sm={4}>
            <Paper
              sx={{
                ...cardSx,
                p: {
                  xs: 1.5,
                  sm: 2,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: colors.secondary,
                  fontWeight: 600,
                }}
              >
                Collection
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 14,
                  lineHeight: 1.3,
                  fontWeight: 600,
                  color: colors.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {collectionData.collectionTitle}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Paper
              sx={{
                ...cardSx,
                p: {
                  xs: 1.5,
                  sm: 2,
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  color: colors.secondary,
                  fontWeight: 600,
                }}
              >
                Status
              </Typography>

              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label="Collection Active"
                  size="small"
                  sx={{
                    height: 25,
                    borderRadius: "7px",
                    fontSize: 10,
                    fontWeight: 600,
                    backgroundColor: colors.successSoft,
                    color: colors.success,
                    border: "none",
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {productList.length === 0 ? (
          <Paper
            sx={{
              ...cardSx,
              minHeight: 350,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              p: 4,
            }}
          >
            <Box>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: "14px",
                  mx: "auto",
                  mb: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                }}
              >
                <Inventory2OutlinedIcon sx={{ fontSize: 28 }} />
              </Box>

              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 22,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                No products yet
              </Typography>

              <Typography
                sx={{
                  mt: 0.7,
                  fontSize: 12,
                  color: colors.secondary,
                  maxWidth: 360,
                  mx: "auto",
                }}
              >
                Add products to this collection to start building your
                catalogue.
              </Typography>

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={handleAddProductClick}
                sx={{
                  mt: 2,
                  height: 40,
                  px: 2,
                  borderRadius: "9px",
                  backgroundColor: colors.accent,
                  textTransform: "none",
                  fontSize: 12,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isDark ? "#C5AC8C" : "#705B44",
                    boxShadow: "none",
                  },
                }}
              >
                Add Product
              </Button>
            </Box>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {productList.map((item) => {
              const product = item.product || {};
              const image = item.productImage?.[0];
              const address = item.address || {};
              const slot = item.slots || {};
              const bookedDates = slot.bookedDate || [];

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={item._id || product._id}
                >
                  <Card
                    sx={{
                      ...cardSx,
                      height: "100%",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      transition:
                        "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: isDark
                          ? "0 16px 42px rgba(0,0,0,.24)"
                          : "0 16px 42px rgba(50,40,30,.08)",
                        borderColor: isDark
                          ? "rgba(209,185,154,.25)"
                          : "rgba(128,107,82,.18)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        height: {
                          xs: 220,
                          sm: 210,
                          md: 225,
                        },
                        backgroundColor: colors.cardAlt,
                        overflow: "hidden",
                      }}
                    >
                      {image ? (
                        <CardMedia
                          component="img"
                          image={image}
                          alt={product.title || "Product"}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            p: 1.5,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: colors.secondary,
                          }}
                        >
                          <Box textAlign="center">
                            <ImageNotSupportedOutlinedIcon
                              sx={{ fontSize: 32 }}
                            />
                            <Typography
                              sx={{
                                mt: 0.5,
                                fontSize: 10,
                                color: colors.secondary,
                              }}
                            >
                              No image
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,.58) 0%, rgba(0,0,0,.05) 48%, transparent 70%)",
                          pointerEvents: "none",
                        }}
                      />

                      <Tooltip title="Remove from collection">
                        <span>
                          <IconButton
                            onClick={() => openRemoveDialog(item)}
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 10,
                              width: 34,
                              height: 34,
                              borderRadius: "9px",
                              backgroundColor: "rgba(255,255,255,.94)",
                              color: colors.danger,
                              boxShadow: "0 3px 12px rgba(0,0,0,.16)",
                              "&:hover": {
                                backgroundColor: colors.dangerSoft,
                                color: colors.danger,
                              },
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Typography
                        sx={{
                          position: "absolute",
                          left: 13,
                          right: 13,
                          bottom: 11,
                          color: "#fff",
                          fontSize: 15,
                          lineHeight: 1.3,
                          fontWeight: 650,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          textShadow: "0 1px 3px rgba(0,0,0,.35)",
                        }}
                      >
                        {product.title || "Untitled Product"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        p: 1.8,
                        display: "flex",
                        flexDirection: "column",
                        flex: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.7}
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ mb: 1.2 }}
                      >
                        {product.size && (
                          <Chip
                            icon={
                              <StraightenOutlinedIcon sx={{ fontSize: 14 }} />
                            }
                            label={`Size ${product.size}`}
                            size="small"
                            sx={{
                              height: 25,
                              borderRadius: "7px",
                              fontSize: 10,
                              color: colors.secondary,
                              backgroundColor: colors.accentSoft,
                              "& .MuiChip-icon": {
                                color: colors.accent,
                              },
                            }}
                          />
                        )}

                        {product.rentAmount !== undefined &&
                          product.rentAmount !== null && (
                            <Chip
                              icon={
                                <CurrencyRupeeRoundedIcon
                                  sx={{ fontSize: 14 }}
                                />
                              }
                              label={product.rentAmount}
                              size="small"
                              sx={{
                                height: 25,
                                borderRadius: "7px",
                                fontSize: 10,
                                fontWeight: 650,
                                color: colors.success,
                                backgroundColor: colors.successSoft,
                                "& .MuiChip-icon": {
                                  color: colors.success,
                                },
                              }}
                            />
                          )}
                      </Stack>

                      {product.description && (
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            lineHeight: 1.6,
                            color: colors.secondary,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: 37,
                          }}
                        >
                          {product.description}
                        </Typography>
                      )}

                      <Divider
                        sx={{
                          my: 1.4,
                          borderColor: colors.border,
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        <CalendarMonthOutlinedIcon
                          sx={{
                            fontSize: 17,
                            color: colors.accent,
                            mt: 0.1,
                          }}
                        />

                        <Box>
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                              color: colors.secondary,
                              fontWeight: 600,
                            }}
                          >
                            Available From
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.15,
                              fontSize: 11.5,
                              color: colors.text,
                              fontWeight: 500,
                            }}
                          >
                            {slot.availableDate || "Not specified"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.8,
                          mb: 1,
                        }}
                      >
                        <LocationOnOutlinedIcon
                          sx={{
                            fontSize: 17,
                            color: colors.accent,
                            mt: 0.1,
                          }}
                        />

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 9.5,
                              textTransform: "uppercase",
                              letterSpacing: ".06em",
                              color: colors.secondary,
                              fontWeight: 600,
                            }}
                          >
                            Location
                          </Typography>

                          <Typography
                            sx={{
                              mt: 0.15,
                              fontSize: 11.5,
                              color: colors.text,
                              fontWeight: 500,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {[address.city, address.state]
                              .filter(Boolean)
                              .join(", ") || "Not specified"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ mt: "auto", pt: 0.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 0.8,
                          }}
                        >
                          <EventBusyOutlinedIcon
                            sx={{
                              fontSize: 17,
                              color:
                                bookedDates.length > 0
                                  ? colors.danger
                                  : colors.secondary,
                              mt: 0.1,
                            }}
                          />

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 9.5,
                                textTransform: "uppercase",
                                letterSpacing: ".06em",
                                color: colors.secondary,
                                fontWeight: 600,
                              }}
                            >
                              Booked Dates
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.15,
                                fontSize: 11,
                                lineHeight: 1.5,
                                color:
                                  bookedDates.length > 0
                                    ? colors.danger
                                    : colors.secondary,
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                              }}
                            >
                              {bookedDates.length > 0
                                ? bookedDates.join(", ")
                                : "No booked dates"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      <Dialog
        open={removeDialog}
        onClose={closeRemoveDialog}
        fullWidth
        maxWidth="xs"
        transitionDuration={0}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: colors.card,
            backgroundImage: "none",
            border: `1px solid ${colors.border}`,
            boxShadow: isDark
              ? "0 20px 60px rgba(0,0,0,.45)"
              : "0 20px 60px rgba(50,40,30,.15)",
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.dangerSoft,
                color: colors.danger,
                flexShrink: 0,
              }}
            >
              <DeleteOutlineRoundedIcon sx={{ fontSize: 22 }} />
            </Box>

            <IconButton
              onClick={closeRemoveDialog}
              disabled={removing}
              size="small"
              sx={{
                color: colors.secondary,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: colors.accentSoft,
                  color: colors.text,
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Box>

          <Typography
            sx={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 22,
              fontWeight: 600,
              color: colors.text,
            }}
          >
            Remove Product?
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: 12.5,
              lineHeight: 1.65,
              color: colors.secondary,
            }}
          >
            Are you sure you want to remove{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 650,
                color: colors.text,
              }}
            >
              {selectedProduct?.product?.title || "this product"}
            </Box>{" "}
            from this collection?
          </Typography>

          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: "10px",
              backgroundColor: colors.dangerSoft,
              border: `1px solid ${isDark ? "rgba(217,148,148,.16)" : "rgba(181,69,69,.12)"}`,
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: 1.5,
                color: colors.danger,
              }}
            >
              This will only remove the product from this collection. The
              product itself will not be deleted.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3 },
            pb: { xs: 2.5, sm: 3 },
            pt: 0,
            gap: 1,
          }}
        >
          <Button
            onClick={closeRemoveDialog}
            disabled={removing}
            sx={{
              height: 40,
              px: 2,
              borderRadius: "9px",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              color: colors.secondary,
              border: `1px solid ${colors.border}`,
              "&:hover": {
                backgroundColor: colors.accentSoft,
                borderColor: colors.accent,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleRemoveProduct}
            disabled={removing}
            variant="contained"
            startIcon={
              removing ? (
                <CircularProgress size={15} sx={{ color: "#fff" }} />
              ) : (
                <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
              )
            }
            sx={{
              height: 40,
              px: 2,
              borderRadius: "9px",
              textTransform: "none",
              fontSize: 12,
              fontWeight: 600,
              backgroundColor: colors.danger,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: isDark ? "#C47F7F" : "#963939",
                boxShadow: "none",
              },
            }}
          >
            {removing ? "Removing..." : "Remove Product"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectionProducts;
